/**
 * Клиент бэкенда портала.
 *
 * Всё пространство `/api` принадлежит бэкенду — фронт ходит только туда и никогда
 * напрямую в основной бэкенд. Свой API-ключ (если пользователь его ввёл) уходит
 * заголовком X-Api-Key и хранится только в этом браузере.
 */
import type {
  CatalogResponse,
  ModelDetail,
  PortalConfig,
  PriceResponse,
  RunRequest,
  RunResponse,
  TaskResponse,
  UploadedFile,
  UpstreamBodyPreview,
  ValidationError,
} from "./types";

export const API_BASE = "/api";
const API_KEY_STORAGE = "phygc-portal-api-key";

export function getApiKey(): string | null {
  return window.localStorage.getItem(API_KEY_STORAGE);
}

/** Подписчики на смену ключа: без ключа действия недоступны, и UI обязан реагировать сразу. */
const keyListeners = new Set<() => void>();

export function subscribeApiKey(listener: () => void): () => void {
  keyListeners.add(listener);
  return () => keyListeners.delete(listener);
}

export function setApiKey(key: string | null): void {
  if (key && key.trim()) window.localStorage.setItem(API_KEY_STORAGE, key.trim());
  else window.localStorage.removeItem(API_KEY_STORAGE);
  keyListeners.forEach((listener) => listener());
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly errors: ValidationError[] = [],
    readonly raw?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

function describeDetail(detail: unknown): { message: string; errors: ValidationError[] } {
  if (typeof detail === "string") return { message: detail, errors: [] };
  if (detail && typeof detail === "object") {
    const record = detail as Record<string, unknown>;
    const errors = Array.isArray(record.errors) ? (record.errors as ValidationError[]) : [];
    const message = typeof record.message === "string" ? record.message : JSON.stringify(detail);
    return { message, errors };
  }
  return { message: "Request failed", errors: [] };
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const key = getApiKey();
  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(key ? { "X-Api-Key": key } : {}),
      ...init.headers,
    },
  });

  const text = await response.text();
  const payload: unknown = text ? JSON.parse(text) : null;

  if (!response.ok) {
    const detail = (payload as { detail?: unknown } | null)?.detail ?? payload;
    const { message, errors } = describeDetail(detail);
    throw new ApiError(response.status, message, errors, payload);
  }
  return payload as T;
}

const post = <T>(path: string, body: unknown) =>
  request<T>(path, { method: "POST", body: JSON.stringify(body) });

/**
 * Загрузка файла. Отдельно от `request`: тело — FormData, и заголовок
 * Content-Type должен проставить браузер вместе с boundary.
 */
async function upload(file: File): Promise<UploadedFile> {
  const key = getApiKey();
  const form = new FormData();
  form.append("file", file);

  const response = await fetch(`${API_BASE}/uploads`, {
    method: "POST",
    body: form,
    headers: key ? { "X-Api-Key": key } : undefined,
  });

  const text = await response.text();
  const payload: unknown = text ? JSON.parse(text) : null;
  if (!response.ok) {
    const detail = (payload as { detail?: unknown } | null)?.detail ?? payload;
    const { message, errors } = describeDetail(detail);
    throw new ApiError(response.status, message, errors, payload);
  }
  return payload as UploadedFile;
}

export const api = {
  upload,

  config: () => request<PortalConfig>("/config"),

  catalog: (refresh = false) =>
    request<CatalogResponse>(`/models${refresh ? "?refresh=true" : ""}`),

  model: (slug: string) => request<ModelDetail>(`/models/${encodeURIComponent(slug)}`),

  preview: (slug: string, body: RunRequest) =>
    post<UpstreamBodyPreview>(`/models/${encodeURIComponent(slug)}/preview`, body),

  run: (slug: string, body: RunRequest) =>
    post<RunResponse>(`/models/${encodeURIComponent(slug)}`, body),

  estimate: (slug: string, body: RunRequest) =>
    post<PriceResponse>(`/models/${encodeURIComponent(slug)}/estimate`, body),

  task: (taskId: number) => request<TaskResponse>(`/tasks/${taskId}`),

  cancelTask: (taskId: number) => post<unknown>(`/tasks/${taskId}/cancel`, null),

  workspaces: () => request<unknown>("/workspaces"),

  runWorkspace: (workspaceId: number, body: unknown) =>
    post<unknown>(`/workspaces/${workspaceId}/run`, body),

  pipeline: (pipelineId: number) => request<unknown>(`/workspaces/pipelines/${pipelineId}`),

  projects: () => request<unknown>("/projects"),

  projectFrames: (projectId: number) => request<unknown>(`/projects/${projectId}/frames`),

  frame: (frameId: string) => request<unknown>(`/frames/${frameId}`),

  /** `overrides` — `{ data: { <uuid ноды>: { inputs, params } } }`; без него фрейм идёт на сохранённых значениях. */
  runFrame: (frameId: string, overrides: unknown = null) =>
    post<unknown>(`/frames/${frameId}/run`, overrides),

  frameRun: (frameId: string, runId: string) =>
    request<unknown>(`/frames/${frameId}/runs/${runId}`),

  frameRunResults: (frameId: string, runId: string) =>
    request<unknown>(`/frames/${frameId}/runs/${runId}/results`),

  runFrameByToken: (token: string, overrides: unknown = null) =>
    request<unknown>("/frames/run-by-token", {
      method: "POST",
      headers: { "X-Frame-Token": token },
      body: JSON.stringify(overrides),
    }),
};
